import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { CurrencyRupeeIcon, EyeIcon, MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  DetailCard,
  DetailItem,
  Field,
  FormActions,
  FormCard,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import {
  useActiveWarehouses,
  useCancelDispatch,
  useCreateDispatch,
  useDispatch,
  useDispatchableOrders,
  useDispatchesPage,
  useDispatchSummary,
  usePendingSalesOrder,
  useRaiseInvoiceFromChallan,
} from '../../hooks/useWarehouses';
import { DispatchInput } from '../../services/warehouse.service';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { BatchPicker, BatchSelection, batchList, batchTotal } from '../../components/common/BatchPicker';
import { defaultRowsPerPage } from '../../lib/preferences';
import { DispatchDocuments } from '../../components/sales/DispatchDocuments';
import { SerialPicker } from '../../components/common/SerialPicker';

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const round4 = (value: number) => Math.round(value * 10000) / 10000;

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const StatusBadge: React.FC<{ status: 'posted' | 'cancelled' }> = ({ status }) => (
  <span className={cn(badgeClass, status === 'posted' ? 'bg-green-50 text-green-700 ring-green-600/20' : 'bg-red-50 text-red-700 ring-red-600/20')}>
    {status === 'posted' ? 'Dispatched' : 'Cancelled'}
  </span>
);

interface LineState {
  quantity: string;
  batchNumber: string;
  heatNumber: string;
  // batch id -> quantity taken from it
  batches: BatchSelection;
  // The units that go, for a product kept by serial number
  serials: string[];
}
const emptyLine: LineState = { quantity: '', batchNumber: '', heatNumber: '', batches: {}, serials: [] };

interface DispatchFormProps {
  initialOrderId: string;
  isPending: boolean;
  onCancel: () => void;
  onSubmit: (data: DispatchInput) => void;
}

const DispatchForm: React.FC<DispatchFormProps> = ({ initialOrderId, isPending, onCancel, onSubmit }) => {
  const [orderId, setOrderId] = useState(initialOrderId);
  const { data: orders = [], isLoading: ordersLoading } = useDispatchableOrders();
  const { data: order, isLoading: orderLoading } = usePendingSalesOrder(orderId || undefined);
  const { data: warehouses = [] } = useActiveWarehouses();

  const [header, setHeader] = useState({ warehouseId: '', dispatchDate: today(), transporter: '', vehicleNumber: '', lrNumber: '', packages: '', shippingAddress: '', notes: '' });
  const [lines, setLines] = useState<Record<string, LineState>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Another order has other lines and another address
  useEffect(() => {
    setLines({});
    setErrors({});
  }, [orderId]);
  useEffect(() => {
    if (order) setHeader((prev) => ({ ...prev, shippingAddress: order.shippingAddress ?? '' }));
  }, [order]);

  const pending = (order?.lines ?? []).filter((line) => line.pendingQuantity > 0);
  const lineOf = (id: string) => lines[id] ?? emptyLine;
  const setLineBatches = (id: string, batches: BatchSelection) => {
    setLines((prev) => ({ ...prev, [id]: { ...(prev[id] ?? emptyLine), batches } }));
    setErrors((prev) => {
      const { [`line-${id}`]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  const setLineSerials = (id: string, serials: string[]) => {
    setLines((prev) => ({ ...prev, [id]: { ...(prev[id] ?? emptyLine), serials } }));
    setErrors((prev) => {
      const { [`line-${id}`]: _cleared, ...rest } = prev;
      return rest;
    });
  };
  const setLine = (id: string, field: 'quantity' | 'batchNumber' | 'heatNumber', value: string) => {
    setLines((prev) => ({ ...prev, [id]: { ...(prev[id] ?? emptyLine), [field]: value } }));
    setErrors((prev) => {
      const { [`line-${id}`]: _cleared, lines: _all, ...rest } = prev;
      return rest;
    });
  };
  const setHeaderField = (field: keyof typeof header) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setHeader((prev) => ({ ...prev, [field]: event.target.value }));

  // As much of each line as is still to go and in stock
  const fillAll = () =>
    setLines((prev) =>
      Object.fromEntries(
        pending.map((line) => [line.salesOrderItemId, { ...(prev[line.salesOrderItemId] ?? emptyLine), quantity: String(Math.max(0, Math.min(line.pendingQuantity, line.stockOnHand))) }]),
      ),
    );

  const totals = pending.reduce(
    (sum, line) => {
      const quantity = Number(lineOf(line.salesOrderItemId).quantity) || 0;
      return { quantity: round4(sum.quantity + quantity), value: sum.value + quantity * line.unitPrice };
    },
    { quantity: 0, value: 0 },
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!orderId) next.orderId = 'Choose the sales order being dispatched';
    if (!header.dispatchDate) next.dispatchDate = 'Enter the dispatch date';
    else if (header.dispatchDate > today()) next.dispatchDate = 'The dispatch date cannot be in the future';
    const packages = header.packages.trim() === '' ? null : Number(header.packages);
    if (packages !== null && (!Number.isInteger(packages) || packages < 0)) next.packages = 'Enter a whole number';

    const items: DispatchInput['items'] = [];
    pending.forEach((line) => {
      const state = lineOf(line.salesOrderItemId);
      if (state.quantity.trim() === '' || Number(state.quantity) === 0) return;
      const quantity = Number(state.quantity);
      const key = `line-${line.salesOrderItemId}`;
      if (!isQuantity(quantity) || quantity < 0) next[key] = 'Use a quantity with up to 4 decimals';
      else if (quantity > line.pendingQuantity) next[key] = `Only ${formatNumber(line.pendingQuantity)} are still to go`;
      else if (quantity > line.stockOnHand) next[key] = `Only ${formatNumber(line.stockOnHand)} in stock`;
      else if (batchTotal(state.batches) > quantity) next[key] = `${formatNumber(batchTotal(state.batches))} is taken from batches, more than the ${formatNumber(quantity)} being dispatched`;
      else if (line.isSerialised && !Number.isInteger(quantity)) next[key] = 'This product is kept by serial number; dispatch whole units';
      else if (line.isSerialised && state.serials.length !== quantity) next[key] = `Choose ${formatNumber(quantity)} serial number(s); ${formatNumber(state.serials.length)} chosen`;
      else items.push({ salesOrderItemId: line.salesOrderItemId, quantity, batchNumber: state.batchNumber.trim() || null, heatNumber: state.heatNumber.trim() || null, batches: batchList(state.batches), ...(line.isSerialised ? { serialNumbers: state.serials } : {}) });
    });
    if (orderId && items.length === 0 && !Object.keys(next).some((key) => key.startsWith('line-'))) next.lines = 'Enter the quantity dispatched for at least one line';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    onSubmit({
      salesOrderId: orderId,
      warehouseId: header.warehouseId || undefined,
      dispatchDate: header.dispatchDate,
      transporter: header.transporter.trim() || null,
      vehicleNumber: header.vehicleNumber.trim() || null,
      lrNumber: header.lrNumber.trim() || null,
      packages,
      shippingAddress: header.shippingAddress.trim() || null,
      notes: header.notes.trim() || null,
      items,
    });
  };

  if (!ordersLoading && orders.length === 0 && !initialOrderId) {
    return (
      <div className="py-6 text-center">
        <CurrencyRupeeIcon className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm font-medium text-gray-900">No sales order is waiting to be dispatched</p>
        <p className="mt-1 text-sm text-gray-500">Goods are dispatched against a confirmed sales order.</p>
        <Link to="/sales" className={cn(primaryButtonClass, 'mt-4')}>
          Go to Sales Orders
        </Link>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-5">
      <FormCard title="Dispatch" description="Which order is going out, and how">
        <Field label="Sales order" htmlFor="dc-order" required error={errors.orderId} className="sm:col-span-2">
          <select id="dc-order" value={orderId} onChange={(event) => setOrderId(event.target.value)} className={cn(inputClass, errors.orderId && inputErrorClass)}>
            <option value="">Select…</option>
            {orderId && order && !orders.some((candidate) => candidate.id === orderId) && <option value={orderId}>{order.orderNumber} · {order.customerName}</option>}
            {orders.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.orderNumber} · {candidate.customerName ?? 'No customer'} · {formatNumber(candidate.pendingQuantity)} to go
              </option>
            ))}
          </select>
        </Field>
        <Field label="Dispatch date" htmlFor="dc-date" required error={errors.dispatchDate}>
          <input id="dc-date" type="date" max={today()} value={header.dispatchDate} onChange={setHeaderField('dispatchDate')} className={cn(inputClass, errors.dispatchDate && inputErrorClass)} />
        </Field>
        <Field label="Take from warehouse" htmlFor="dc-warehouse" hint="Automatic takes from the default warehouse first, then the others">
          <select id="dc-warehouse" value={header.warehouseId} onChange={setHeaderField('warehouseId')} className={inputClass}>
            <option value="">Automatic</option>
            {warehouses.map((warehouse) => (
              <option key={warehouse.id} value={warehouse.id}>
                {warehouse.name} ({warehouse.code})
              </option>
            ))}
          </select>
        </Field>
        <Field label="Transporter" htmlFor="dc-transporter">
          <input id="dc-transporter" type="text" maxLength={100} value={header.transporter} onChange={setHeaderField('transporter')} className={inputClass} />
        </Field>
        <Field label="Vehicle no." htmlFor="dc-vehicle">
          <input id="dc-vehicle" type="text" maxLength={30} value={header.vehicleNumber} onChange={setHeaderField('vehicleNumber')} className={inputClass} />
        </Field>
        <Field label="LR / tracking no." htmlFor="dc-lr">
          <input id="dc-lr" type="text" maxLength={50} value={header.lrNumber} onChange={setHeaderField('lrNumber')} className={inputClass} />
        </Field>
        <Field label="Packages" htmlFor="dc-packages" error={errors.packages} hint="Boxes, bundles or pallets">
          <input id="dc-packages" type="number" min="0" step="1" value={header.packages} onChange={setHeaderField('packages')} className={cn(inputClass, errors.packages && inputErrorClass)} />
        </Field>
        <Field label="Deliver to" htmlFor="dc-address" className="sm:col-span-2">
          <textarea id="dc-address" rows={2} maxLength={500} value={header.shippingAddress} onChange={setHeaderField('shippingAddress')} className={inputClass} />
        </Field>
        <Field label="Notes" htmlFor="dc-notes" className="sm:col-span-2">
          <input id="dc-notes" type="text" maxLength={1000} value={header.notes} onChange={setHeaderField('notes')} className={inputClass} />
        </Field>
      </FormCard>

      {orderId && (
        <section className="rounded-lg border border-gray-200 bg-white">
          <div className="flex flex-col gap-2 rounded-t-lg border-b border-gray-200 bg-gray-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">What is going out</h4>
              <p className="mt-0.5 text-xs text-gray-500">Leave a line empty if it is not on this dispatch. The goods leave stock when the challan is saved.</p>
            </div>
            {pending.length > 0 && (
              <button type="button" onClick={fillAll} className="self-start rounded-md px-2.5 py-1 text-xs font-semibold text-indigo-600 ring-1 ring-inset ring-indigo-200 hover:bg-indigo-50 sm:self-auto">
                Fill everything in stock
              </button>
            )}
          </div>
          {orderLoading ? (
            <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>
          ) : order && !order.dispatchable ? (
            <p className="px-4 py-6 text-sm text-gray-700">
              {order.orderNumber} is {order.status.replace('_', ' ')}; goods cannot be dispatched against it.
            </p>
          ) : pending.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-500">Everything on this order has been dispatched.</p>
          ) : (
            <div className="divide-y divide-gray-100">
              {pending.map((line) => {
                const state = lineOf(line.salesOrderItemId);
                const error = errors[`line-${line.salesOrderItemId}`];
                return (
                  <div key={line.salesOrderItemId} className="space-y-3 p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{line.productName}</p>
                        <p className="text-xs text-gray-500">{line.sku}</p>
                      </div>
                      <p className="text-xs text-gray-600 tabular-nums">
                        Ordered {formatNumber(line.orderedQuantity)} · sent {formatNumber(line.dispatchedQuantity)} ·{' '}
                        <span className="font-semibold text-gray-900">{formatNumber(line.pendingQuantity)} {line.unit} to go</span> ·{' '}
                        <span className={line.stockOnHand < line.pendingQuantity ? 'font-medium text-red-600' : undefined}>{formatNumber(line.stockOnHand)} in stock</span>
                      </p>
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <Field label="Dispatch now" htmlFor={`dq-${line.salesOrderItemId}`}>
                        <input
                          id={`dq-${line.salesOrderItemId}`}
                          type="number"
                          min="0"
                          step={QUANTITY_STEP}
                          value={state.quantity}
                          onChange={(event) => setLine(line.salesOrderItemId, 'quantity', event.target.value)}
                          aria-label={`Dispatch quantity of ${line.productName}`}
                          className={cn(inputClass, 'text-right tabular-nums', error && inputErrorClass)}
                        />
                      </Field>
                      <Field label="Batch / lot no." htmlFor={`db-${line.salesOrderItemId}`}>
                        <input id={`db-${line.salesOrderItemId}`} type="text" maxLength={50} value={state.batchNumber} onChange={(event) => setLine(line.salesOrderItemId, 'batchNumber', event.target.value)} className={inputClass} />
                      </Field>
                      <Field label="Heat no." htmlFor={`dh-${line.salesOrderItemId}`}>
                        <input id={`dh-${line.salesOrderItemId}`} type="text" maxLength={50} value={state.heatNumber} onChange={(event) => setLine(line.salesOrderItemId, 'heatNumber', event.target.value)} className={inputClass} />
                      </Field>
                    </div>
                    <BatchPicker productId={line.productId} idPrefix={`dbt-${line.salesOrderItemId}`} value={state.batches} onChange={(batches) => setLineBatches(line.salesOrderItemId, batches)} label="Taken from batches (their numbers are printed when none is typed above)" />
                    {line.isSerialised && <SerialPicker productId={line.productId} productName={line.productName} wanted={Number(state.quantity) || 0} value={state.serials} onChange={(serials) => setLineSerials(line.salesOrderItemId, serials)} />}
                    {error && <p className="text-sm text-red-600">{error}</p>}
                  </div>
                );
              })}
              <div className="flex flex-wrap items-center justify-between gap-2 bg-gray-50 px-4 py-3 text-sm">
                <span className="text-gray-600">
                  Dispatching <span className="font-semibold text-gray-900">{formatNumber(totals.quantity)}</span>
                </span>
                <span className="font-semibold text-gray-900 tabular-nums">{formatMoney(totals.value)} before tax</span>
              </div>
            </div>
          )}
          {errors.lines && <p className="border-t border-gray-200 px-4 py-3 text-sm text-red-600">{errors.lines}</p>}
        </section>
      )}

      <FormActions>
        <button type="button" onClick={onCancel} disabled={isPending} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending || !orderId || pending.length === 0} className={primaryButtonClass}>
          {isPending ? 'Saving…' : 'Dispatch Goods'}
        </button>
      </FormActions>
    </form>
  );
};

// One challan with its lines; a challan can be cancelled with a reason
const ChallanView: React.FC<{ challanId: string; canCancel: boolean; canInvoice: boolean; onClose: () => void }> = ({ challanId, canCancel, canInvoice, onClose }) => {
  const { data: challan, isLoading, isError, error } = useDispatch(challanId);
  const cancelDispatch = useCancelDispatch();
  const raiseInvoice = useRaiseInvoiceFromChallan();
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');

  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !challan) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The delivery challan could not be loaded.')}</p>;

  const handleInvoice = () => {
    raiseInvoice.mutate(challan.id, {
      onSuccess: (invoice) => toast.success(`Draft invoice ${invoice.invoiceNumber} raised. Check and issue it under Invoices.`),
      onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The invoice could not be raised')),
    });
  };

  const handleCancel = () => {
    cancelDispatch.mutate(
      { id: challan.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`${challan.challanNumber} cancelled`);
          setCancelling(false);
        },
        onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The delivery challan could not be cancelled')),
      },
    );
  };

  return (
    <div className="space-y-5">
      <DetailCard title="Delivery challan" columns={4}>
        <DetailItem label="Sales order">{challan.salesOrderNumber}</DetailItem>
        <DetailItem label="Customer">{challan.customerName || '—'}</DetailItem>
        <DetailItem label="Dispatch date">{formatDate(challan.dispatchDate)}</DetailItem>
        <DetailItem label="Status"><StatusBadge status={challan.status} /></DetailItem>
        <DetailItem label="Transporter">{challan.transporter || '—'}</DetailItem>
        <DetailItem label="Vehicle">{challan.vehicleNumber || '—'}</DetailItem>
        <DetailItem label="LR / tracking">{challan.lrNumber || '—'}</DetailItem>
        <DetailItem label="Packages">{challan.packages ?? '—'}</DetailItem>
        <DetailItem label="Value before tax">{formatMoney(challan.totalValue)}</DetailItem>
        <DetailItem label="Invoice">{challan.invoiceNumber || 'Not invoiced'}</DetailItem>
        <DetailItem label="Deliver to" className="col-span-2">
          <span className="whitespace-pre-line font-normal">{challan.shippingAddress || '—'}</span>
        </DetailItem>
        {challan.notes && (
          <DetailItem label="Notes" className="col-span-2 lg:col-span-4">
            <span className="font-normal">{challan.notes}</span>
          </DetailItem>
        )}
        {challan.status === 'cancelled' && (
          <DetailItem label="Cancelled because" className="col-span-2 lg:col-span-4">
            <span className="font-normal text-red-700">{challan.cancelReason || '—'}</span>
          </DetailItem>
        )}
      </DetailCard>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Product</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Ordered</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Dispatched</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Value</th>
              <th scope="col" className={thClass}>Batch / lot</th>
              <th scope="col" className={thClass}>Heat no.</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(challan.items ?? []).map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2.5">
                  <p className="text-sm font-medium text-gray-900">{item.productName}</p>
                  <p className="text-xs text-gray-500">{item.sku}</p>
                  {(item.serialNumbers ?? []).length > 0 && <p className="mt-0.5 max-w-md text-xs text-gray-600">Serial numbers: {(item.serialNumbers ?? []).join(', ')}</p>}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-600 tabular-nums">{formatNumber(item.orderedQuantity)}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">
                  {formatNumber(item.quantity)} <span className="font-normal text-gray-500">{item.unit}</span>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatMoney(item.quantity * item.unitPrice)}</td>
                <td className="px-4 py-2.5 text-sm text-gray-700">{item.batchNumber || '—'}</td>
                <td className="px-4 py-2.5 text-sm text-gray-700">{item.heatNumber || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!cancelling && <DispatchDocuments challan={challan} canEdit={canCancel} />}

      {cancelling && (
        <div className="space-y-3 rounded-lg bg-red-50 p-4 ring-1 ring-red-600/20">
          <p className="text-sm text-red-800">
            The {formatNumber(challan.totalQuantity)} unit(s) return to stock and {challan.salesOrderNumber} has them to send once more.
            {challan.gatePass ? ` Gate pass ${challan.gatePass.gatePassNumber} is cancelled with it.` : ''}
          </p>
          <Field label="Reason" htmlFor="dc-cancel-reason" required>
            <input id="dc-cancel-reason" type="text" maxLength={300} autoFocus value={reason} onChange={(event) => setReason(event.target.value)} className={inputClass} />
          </Field>
        </div>
      )}

      <FormActions>
        {cancelling ? (
          <>
            <button type="button" onClick={() => setCancelling(false)} disabled={cancelDispatch.isPending} className={secondaryButtonClass}>
              Back
            </button>
            <button type="button" onClick={handleCancel} disabled={cancelDispatch.isPending || reason.trim().length < 3} className={dangerButtonClass}>
              {cancelDispatch.isPending ? 'Cancelling…' : 'Cancel Challan'}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={onClose} className={secondaryButtonClass}>
              Close
            </button>
            {challan.status === 'posted' && canCancel && !challan.invoiceId && (
              <button type="button" onClick={() => setCancelling(true)} disabled={raiseInvoice.isPending} className={secondaryButtonClass}>
                Cancel Challan
              </button>
            )}
            {challan.invoiceId && (
              <Link to="/invoices" className={secondaryButtonClass}>
                Open Invoices
              </Link>
            )}
            {challan.status === 'posted' && canInvoice && !challan.invoiceId && (
              <button type="button" onClick={handleInvoice} disabled={raiseInvoice.isPending} className={primaryButtonClass}>
                {raiseInvoice.isPending ? 'Raising…' : 'Raise Invoice'}
              </button>
            )}
          </>
        )}
      </FormActions>
    </div>
  );
};

const Dispatches: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('sales.create');
  const canCancel = can('sales.update');
  const canInvoice = can('gst.create');
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'posted' | 'cancelled' | ''>('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage);

  // "Dispatch" on a sales order opens the form with that order chosen
  const orderFromLink = searchParams.get('so') ?? '';
  const [formOpen, setFormOpen] = useState(!!orderFromLink);
  const [viewingId, setViewingId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching, refetch } = useDispatchesPage({ page, limit, search: search || undefined, status: statusFilter });
  const { data: summary } = useDispatchSummary();
  const createDispatch = useCreateDispatch();

  const challans = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const viewing = challans.find((challan) => challan.id === viewingId);

  useEffect(() => {
    if (data && page > data.totalPages) setPage(data.totalPages);
  }, [data, page]);

  const hasFilters = !!searchInput || !!statusFilter;
  const closeForm = () => {
    if (createDispatch.isPending) return;
    setFormOpen(false);
    if (orderFromLink) setSearchParams({}, { replace: true });
  };

  const handleSubmit = (input: DispatchInput) => {
    createDispatch.mutate(input, {
      onSuccess: (saved) => {
        toast.success(`${saved.challanNumber}: ${formatNumber(saved.totalQuantity)} unit(s) dispatched`);
        setFormOpen(false);
        if (orderFromLink) setSearchParams({}, { replace: true });
        setViewingId(saved.id);
      },
      onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The delivery challan could not be saved')),
    });
  };

  const stats = [
    { label: 'Challans', value: summary ? formatNumber(summary.posted) : '—', hint: `${summary ? formatNumber(summary.cancelled) : '—'} cancelled` },
    { label: 'This month', value: summary ? formatNumber(summary.thisMonth) : '—', hint: 'Dispatches booked' },
    { label: 'Orders awaiting dispatch', value: summary ? formatNumber(summary.ordersAwaitingDispatch) : '—', hint: `${summary ? formatNumber(summary.unitsAwaitingDispatch) : '—'} unit(s) still to go` },
    { label: 'Not invoiced', value: summary ? formatNumber(summary.notInvoiced) : '—', hint: 'Challans without an invoice' },
  ];
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Dispatch</h1>
          <p className="mt-1 text-sm text-gray-500">Delivery challans: what left against sales orders, dispatch by dispatch. The goods leave stock when a challan is saved.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/sales" className={secondaryButtonClass}>
            <CurrencyRupeeIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Sales Orders
          </Link>
          {canCreate && (
            <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
              <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
              Dispatch Goods
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by challan, order, customer, product, vehicle, LR, batch or heat number" aria-label="Search delivery challans" className={cn(inputClass, 'pl-9')} />
          </div>
          <select
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value as typeof statusFilter);
              setPage(1);
            }}
            aria-label="Status"
            className={cn(inputClass, 'lg:w-44')}
          >
            <option value="">All statuses</option>
            <option value="posted">Dispatched</option>
            <option value="cancelled">Cancelled</option>
          </select>
          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                setSearch('');
                setStatusFilter('');
                setPage(1);
              }}
              className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
            >
              Clear
            </button>
          )}
        </div>

        {isError ? (
          <div className="p-10 text-center">
            <p className="text-sm text-gray-700">{getApiErrorMessage(error, 'The delivery challans could not be loaded.')}</p>
            <button type="button" onClick={() => refetch()} className={cn(secondaryButtonClass, 'mt-4')}>
              Try again
            </button>
          </div>
        ) : (
          <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className={thClass}>Challan</th>
                  <th scope="col" className={thClass}>Sales order</th>
                  <th scope="col" className={thClass}>Customer</th>
                  <th scope="col" className={thClass}>Vehicle / gate pass</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Quantity</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Value</th>
                  <th scope="col" className={thClass}>Status</th>
                  <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  Array.from({ length: 4 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 8 }).map((__, cell) => (
                        <td key={cell} className="px-4 py-4">
                          <div className="h-4 animate-pulse rounded bg-gray-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : challans.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center">
                      <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No delivery challan matches' : 'No dispatches yet'}</p>
                      <p className="mt-1 text-sm text-gray-500">{hasFilters ? 'Try a different search or status.' : 'Dispatch goods against a confirmed sales order.'}</p>
                    </td>
                  </tr>
                ) : (
                  challans.map((challan) => (
                    <tr key={challan.id} className={cn('hover:bg-gray-50', challan.status === 'cancelled' && 'bg-gray-50/60')}>
                      <td className="whitespace-nowrap px-4 py-3">
                        <button type="button" onClick={() => setViewingId(challan.id)} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
                          {challan.challanNumber}
                        </button>
                        <p className="mt-0.5 text-xs text-gray-500">{formatDate(challan.dispatchDate)}</p>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{challan.salesOrderNumber}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{challan.customerName || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {challan.vehicleNumber || <span className="text-gray-400">—</span>}
                        {challan.gatePassNumber && (
                          <p className={cn('mt-0.5 whitespace-nowrap text-xs', challan.gatePassStatus === 'out' ? 'text-green-700' : 'text-amber-700')}>
                            {challan.gatePassNumber} · {challan.gatePassStatus === 'out' ? 'out' : 'not yet out'}
                          </p>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{formatNumber(challan.totalQuantity)}</td>
                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{formatMoney(challan.totalValue)}</td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <StatusBadge status={challan.status} />
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <button type="button" onClick={() => setViewingId(challan.id)} title="View" aria-label={`View ${challan.challanNumber}`} className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                          <EyeIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>
              Showing {firstRow}–{lastRow} of {formatNumber(total)}
            </span>
            <label className="flex items-center gap-2">
              Rows per page
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              >
                {PAGE_SIZES.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} title="Dispatch Goods" description="Books a delivery challan. The quantities leave stock straight away." size="2xl">
        {formOpen && <DispatchForm initialOrderId={orderFromLink} isPending={createDispatch.isPending} onCancel={closeForm} onSubmit={handleSubmit} />}
      </Modal>

      <Modal isOpen={!!viewingId && !formOpen} onClose={() => setViewingId(null)} title={viewing?.challanNumber ?? 'Delivery Challan'} size="2xl">
        {viewingId && !formOpen && <ChallanView key={viewingId} challanId={viewingId} canCancel={canCancel} canInvoice={canInvoice} onClose={() => setViewingId(null)} />}
      </Modal>
    </div>
  );
};

export default Dispatches;
